import { useEffect, useMemo } from 'react'
import { useInfiniteQuery, useQueryClient } from 'react-query'
import { apiConfig } from 'services'

// A batch size, not a limit on the number of rows in the list.
const batchSize = 100

export default function useAllRows({
  queryName,
  apiCall,
  params,
  enabled = true,
  refreshSignal
}) {
  const queryClient = useQueryClient()
  const queryKey = [
    queryName,
    {
      allRows: true,
      currentApiInstitute: apiConfig.currentApiInstitute,
      params,
      refreshSignal
    }
  ]
  const query = useInfiniteQuery(
    queryKey,
    async ({ pageParam = 0, signal }) => {
      const response = await apiCall({
        ...params,
        page: pageParam,
        size: batchSize
      })
      // Consuming the signal lets React Query discard requests after a filter
      // or organization change, even though the API wrapper cannot abort HTTP.
      if (signal?.aborted) throw new Error('List request cancelled')
      return response
    },
    {
      enabled,
      getNextPageParam: (lastPage) => lastPage.next ?? undefined
    }
  )
  const { data, hasNextPage, isFetching, isError, fetchNextPage } = query

  useEffect(() => {
    if (enabled && hasNextPage && !isFetching && !isError) {
      fetchNextPage()
    }
  }, [enabled, hasNextPage, isFetching, isError, fetchNextPage])

  const list = useMemo(
    () =>
      data && {
        items: data.pages.flatMap((page) => page.items),
        total: data.pages[0].total
      },
    [data]
  )

  // React Query v3 retains the fetch direction but has no isFetchNextPageError.
  const isFetchNextPageError =
    isError &&
    queryClient.getQueryState(queryKey)?.fetchMeta?.fetchMore?.direction ===
      'forward'

  return { ...query, list, isFetchNextPageError }
}
